import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import { JSX } from 'react';
import * as uuid from 'uuid';
import {
  AVAILS,
  LinearImpressionOverrides,
  makeKey,
  PlanRow,
  RowEdit,
  TYPES_MAP,
  TYPES_TO_NAMES
} from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { isCreativeLive } from '@blisspointmedia/bpm-types/dist/linearBuyingUtils';
import { CreativeMap, CreativeMapItem } from '@src/redux/creative';
import { DATE_FORMAT, DAYS_OF_WEEK } from '@src/utils/date-utils';
import {
  LOCAL_AUDIENCE_CORRECTION_FACTOR,
  TYPE_TO_STATUS_IN_NETWORK_PRICING
} from './linear-buying-constants';

export interface FormattedRow extends PlanRow {
  formattedType: string;
  totalSpend: number;
  impressions: number;
  secondaryImpressions: number;
  cpm: number;
  trp: number;
  orderedCpm?: number;
  rows: FormattedRow[];
}

export type RowsByWeek = Record<string, PlanRow[]>;

/** A creative's share of one week's rotation, keyed by `${week}${isci}`. */
export interface CreativePercent {
  week: string;
  isci: string;
  percent: number;
}

export type CreativePercentMap = Record<string, CreativePercent>;

export interface CampaignMetadata {
  options: {
    label: string;
    value: number;
  }[];
  idToName: Record<string, string>;
  nameToId: Record<string, number>;
}

export type EditsMap = Record<string, PlanRow>;

export interface NetworkMetaData {
  network: string;
  rows: PlanRow[];
  isNewNetwork: boolean;
  hasNewRows: boolean;
}

export type NetworkMetaDataMap = Record<string, NetworkMetaData>;

export interface RotationPricing {
  network: string;
  avail: (typeof AVAILS)[number];
  cost30s: number;
  rotationName: string;
  daypartBegin: string;
  daypartEnd: string;
  daysOfWeek: string[];
  rotationLabel: string;
  status: string;
  market: string | null;
}

export type RotationsAndPricing = Record<string, Record<string, RotationPricing[]>>;

export interface ExtendEditModalData {
  row: PlanRow;
  edits: RowEdit;
  selectedEditToExtend: string | null;
}

interface ConstraintRow {
  network: string;
  avail?: string;
  length?: string;
  rotation?: string;
  length15sUnits?: string;
  length30sUnits?: string;
  length60sUnits?: string;
  spend?: string;
}

export interface Constraints {
  avoid: Record<string, ConstraintRow[]>;
  maximum: Record<string, ConstraintRow[]>;
}

type AudienceEstimateByTimeBlock = Record<string, number>;

export type NielsenEstimates = Record<string, AudienceEstimateByTimeBlock>;

export interface SpendByAvailLength {
  avail: string;
  totalPercentByAvail: number;
  totalSpendByAvail: number;
  // Percent of total spend for each creative length present under this avail, keyed by length.
  [length: number]: number;
}

interface SummaryData {
  totalSpend: number;
  byLength: SpendObj[];
  byNetwork: SpendObj[];
  byRotation: SpendObj[];
  byCreative: SpendObj[];
  byAvailLength: SpendByAvailLength[];
}

/**
 * Corrects the format of a time in 24hr format.
 * Start and end times that come from rotations and pricing don't have a leading 0 for times before 10:00,
 * which doesn't follow the 24hr time standard, and messes up the time input.
 * e.g. 9:00 -> 09:00
 */
export const time24hr = (daypart: string): string => {
  if (!daypart) {
    return '';
  }
  const time = R.pipe(Dfns.parse(new Date(), 'H:mm'), Dfns.format('HH:mm'))(daypart);
  return time;
};

/** Checks dates selected to see if more than one week of data is being displayed */
export const isMultiDatePicker = (weeks: string[]): boolean => weeks.length > 1;

/**
 * Receives a date string and returns the Monday of the following week.
 */
export const getNextWeek = (week: string): string =>
  R.pipe(Dfns.parseISO, Dfns.startOfISOWeek, Dfns.addWeeks(1), Dfns.format(DATE_FORMAT))(week);

/**
 * Since new rows for the same network are initialized to the same values, their keys
 * will be the same (which makes editing new rows not work correctly). Adding a unique id to the
 * end of the key for new rows.
 */
export const makeKeyWithUUID = (row: PlanRow): string => {
  const key = makeKey(row);
  const keyWithUUID = `${key}_${uuid.v4()}`;
  return keyWithUUID;
};

/** formats to week of "M/d/yyyy" from iso */
export const weekFormatter = (date: string): string =>
  `Spots Week of ${Dfns.format('M/d/yyyy', Dfns.parseISO(date))}`;

/**
 * Merges plan rows and edits to reflect the current pending state for a given week.
 */
const mergeRowsAndEdits = (rows: PlanRow[], editsMap: EditsMap): PlanRow[] => {
  if (!rows) {
    return [];
  }
  const mergedRowsAndEdits: PlanRow[] = [];

  for (const row of rows) {
    const { key } = row;
    if (editsMap[key]) {
      const rowWithMergedEdits = { ...editsMap[key], ...editsMap[key].edits };
      mergedRowsAndEdits.push(rowWithMergedEdits);
    } else {
      mergedRowsAndEdits.push(row);
    }
  }

  return mergedRowsAndEdits;
};

/**
 * Merges plan rows and edits to reflect the current pending state for a given week, plus adds
 * new rows that have been added to the plan.
 */
export const mergeAllRows = (
  rows: PlanRow[],
  editsMap: EditsMap,
  newRows: Record<string, PlanRow>
): PlanRow[] => {
  const allRows: PlanRow[] = [...Object.values(newRows), ...mergeRowsAndEdits(rows, editsMap)];

  // Keep the first row for each identity key. This previously filtered by deep equality against a
  // separately-built copy of the duplicate rows, which went wrong in both directions: for edited
  // rows the two copies differed by an `edits` property so nothing matched and the duplicate
  // survived, and for unedited rows every copy matched so all of them were dropped.
  const seenKeys = new Set<string>();
  return allRows.filter(row => {
    const key = makeKey(row);
    if (seenKeys.has(key)) {
      return false;
    }
    seenKeys.add(key);
    return true;
  });
};

/**
 * returns the plan row when searching by week
 */
export const getPlanRow = (planRows: PlanRow[], key: string): PlanRow | undefined =>
  planRows.find(row => row.week === key);

/**
 * returns the plan row when searching by week
 */
export const getFormattedRow = (
  formattedRows: FormattedRow[],
  key: string
): FormattedRow | undefined => formattedRows.find(row => row.week === key);

export const isNew = (formattedRows: FormattedRow[], key: string): boolean => {
  if (!formattedRows) {
    return false;
  }
  return Boolean(formattedRows.find(row => row.week === key)?.isNewRow);
};
/**
 * Check if there are any duplicate rows.
 */
export const checkForDuplicateRows = (
  rowsByWeek: RowsByWeek,
  editsMap: EditsMap,
  newRows: Record<string, PlanRow>
): { hasDuplicates: boolean; lines: JSX.Element; duplicates: Record<string, PlanRow> } => {
  const uniqueKeyMap: Record<string, boolean> = {};
  const duplicates: Record<string, PlanRow> = {};
  // Existing rows merged with edits.
  for (const week of R.keys(rowsByWeek)) {
    for (const row of rowsByWeek[week]) {
      const mergedRow = editsMap[row.key] ? { ...row, ...editsMap[row.key].edits } : row;
      const key = makeKey(mergedRow);
      if (uniqueKeyMap[key]) {
        duplicates[key] = mergedRow;
      } else {
        uniqueKeyMap[key] = true;
      }
    }
  }
  // New rows
  for (const row of R.values(newRows)) {
    const key = makeKey(row);
    if (uniqueKeyMap[key]) {
      duplicates[key] = row;
    } else {
      uniqueKeyMap[key] = true;
    }
  }

  const lines = (
    <div>
      <RowSummaryList rows={R.values(duplicates)} />
    </div>
  );

  return { hasDuplicates: !R.isEmpty(duplicates), lines, duplicates };
};

/** One line per row identifying it by week, network, avail, rotation, length, and type. */
const RowSummaryList = ({ rows }: { rows: PlanRow[] }) => (
  <>
    {rows.map(row => {
      const summary = `${row.week} - ${row.network} - ${row.avail} - ${row.rotation} - ${
        row.length
      } - ${TYPES_TO_NAMES[`${row.type}`]}`;
      return <div key={summary}>{summary}</div>;
    })}
  </>
);

/** creates a concatenation of the isci and week */
export const transformToHash = (
  week: string,
  isci: string,
  percent: number
): CreativePercentMap => {
  const creativePercent: CreativePercentMap = {};
  creativePercent[R.concat(week, isci)] = {
    week,
    isci,
    percent
  };
  return creativePercent;
};

/**
 * Checks if the constraint is relevant to the plan row.
 */
const isRelevantConstraint = (row: PlanRow, constraint: ConstraintRow) => {
  const { avail: rowAvail, rotation: rowRotation } = row;
  const { avail: constraintAvail, rotation: constraintRotation } = constraint;

  // If there's no avail and rotation specified for this constraint, then it's relevant for all
  // plan rows for this network.
  if (!constraintAvail && !constraintRotation) {
    return true;
  }

  // If there's no avail specified for this constraint, but there is a rotation and it matches the
  // row rotation, then it is a relevant constraint.
  if (!constraintAvail && constraintRotation && constraintRotation === rowRotation) {
    return true;
  }

  // If there's no rotation specified for this constraint, but there is an avail and it matches the
  // row avail, then it is a relevant constraint.
  if (!constraintRotation && constraintAvail && constraintAvail === rowAvail) {
    return true;
  }

  // If there's both an avail and rotation specified for this constraint, and they both match the
  // row values, then it is a relevant constraint.
  if (
    constraintAvail &&
    constraintRotation &&
    constraintAvail === rowAvail &&
    constraintRotation === rowRotation
  ) {
    return true;
  }

  // If we don't hit on any of the above, then not relevant.
  return false;
};

/**
 * Checks if the plan row is violating the constraint.
 */
const isViolatingConstraint = (
  row: PlanRow,
  constraint: ConstraintRow,
  constraintType: 'avoid' | 'maximum'
) => {
  const { length15sUnits, length30sUnits, length60sUnits, spend, length } = constraint;

  if (constraintType === 'avoid') {
    // We already checked for avail and rotation in isRelevantConstraint, so the last thing to
    // check for avoid constraints is if there's a matching length or if length isn't specified at all.
    if (!length) {
      return true;
    }
    if (length && parseInt(length, 10) === row.length) {
      return true;
    }
  }
  if (constraintType === 'maximum') {
    const rowSpend = row.count * row.cost;
    if (spend && rowSpend > parseInt(spend, 10)) {
      return true;
    }
    if (length15sUnits && row.length === 15 && row.count > parseInt(length15sUnits, 10)) {
      return true;
    }
    if (length30sUnits && row.length === 30 && row.count > parseInt(length30sUnits, 10)) {
      return true;
    }
    if (length60sUnits && row.length === 60 && row.count > parseInt(length60sUnits, 10)) {
      return true;
    }
  }

  return false;
};

/**
 * Check if any edits or new rows violate constraints.
 */
export const checkForConstraints = (
  editsMap: EditsMap,
  newRows: Record<string, PlanRow>,
  constraints: Constraints | undefined
): { isViolatingConstraints: boolean; rows: JSX.Element } => {
  if (!constraints) {
    return { isViolatingConstraints: false, rows: <div /> };
  }
  const avoidViolations: Record<string, PlanRow> = {};
  const maximumViolations: Record<string, PlanRow> = {};

  // Edits live under `.edits` until they're saved, so they have to be merged onto the row before
  // it's checked — otherwise a row edited into violation is measured by its pre-edit values and
  // the confirmation never fires. New rows already carry their values directly.
  const allRows = [
    ...R.values(editsMap).map(row => ({ ...row, ...row.edits })),
    ...R.values(newRows)
  ];

  for (const row of allRows) {
    const networkAvoid = constraints.avoid[row.network];
    const networkMaximum = constraints.maximum[row.network];
    if (networkAvoid) {
      for (const avoid of networkAvoid) {
        if (isRelevantConstraint(row, avoid) && isViolatingConstraint(row, avoid, 'avoid')) {
          avoidViolations[row.key] = row;
        }
      }
    }
    if (networkMaximum) {
      for (const maximum of networkMaximum) {
        if (isRelevantConstraint(row, maximum) && isViolatingConstraint(row, maximum, 'maximum')) {
          maximumViolations[row.key] = row;
        }
      }
    }
  }

  const rows = (
    <div>
      <div>The following rows violate a constraint. Are you sure you want to save?</div>
      {!R.isEmpty(avoidViolations) && (
        <>
          <strong>AVOID</strong>
          <RowSummaryList rows={R.values(avoidViolations)} />
        </>
      )}
      {!R.isEmpty(maximumViolations) && (
        <>
          <strong>MAXIMUM</strong>
          <RowSummaryList rows={R.values(maximumViolations)} />
        </>
      )}
    </div>
  );

  return {
    isViolatingConstraints: !R.isEmpty(avoidViolations) || !R.isEmpty(maximumViolations),
    rows
  };
};

/**
 * Check if any edits or new rows update or override impressions.
 */
export const checkForImpressionOverrides = (
  editsMap: EditsMap,
  newRows: Record<string, PlanRow>
): { isUpdatingImpressions: boolean; listOfRows: JSX.Element } => {
  const impressionsOverrideEdits: Record<string, PlanRow> = {};
  const newImpressionsUpload: Record<string, PlanRow> = {};

  const allRows = [...R.values(editsMap), ...R.values(newRows)];

  for (const row of allRows) {
    if (!row.isNewRow && !R.isNil(row.edits?.impressions)) {
      impressionsOverrideEdits[row.key] = row;
    } else if (row.isNewRow && row.impressions) {
      newImpressionsUpload[row.key] = row;
    }
  }
  const listOfRows = (
    <div>
      <div>
        The following rows are updating impressions or overriding Nielsen Estimates. Did you mean to
        do that? Are you sure you want to save? If not, clear the impressionsOverride column and
        upload again.
      </div>
      {!R.isEmpty(impressionsOverrideEdits) && (
        <>
          <strong>EDITED ROWS</strong>
          <RowSummaryList rows={R.values(impressionsOverrideEdits)} />
        </>
      )}
      {!R.isEmpty(newImpressionsUpload) && (
        <>
          <strong>NEW ROWS</strong>
          <RowSummaryList rows={R.values(newImpressionsUpload)} />
        </>
      )}
    </div>
  );
  if (R.isEmpty(newImpressionsUpload) && R.isEmpty(impressionsOverrideEdits)) {
    return { isUpdatingImpressions: false, listOfRows: <div /> };
  }
  return {
    isUpdatingImpressions: !R.isEmpty(newImpressionsUpload) || !R.isEmpty(impressionsOverrideEdits),
    listOfRows
  };
};

export const processExtendSelected = (
  rowsToExtend: Record<string, PlanRow>,
  week: string,
  existingRows: PlanRow[]
): Record<string, PlanRow> => {
  // Create a map of existing keys for the selected week.
  const existingRowKeys: Record<string, boolean> = {};
  for (const row of existingRows) {
    existingRowKeys[row.key] = true;
  }
  // For each row that we're extending, copy it but change the week and key. Also set the plan_id,
  // order_id, and traffic_id to null because it should be treated like a new row.
  const newRows: Record<string, PlanRow> = {};
  for (const row of R.values(rowsToExtend)) {
    const rowWithNewWeek = {
      ...row,
      week,
      plan_id: null,
      order_id: null,
      traffic_id: null,
      isNewRow: true,
      impressions: 0
    };
    const key = makeKey(rowWithNewWeek);
    // If the key already exists, skip this row so we don't have duplicates for the same week.
    if (existingRowKeys[key]) {
      continue;
    }
    const rowWithNewKey = { ...rowWithNewWeek, key };
    newRows[key] = rowWithNewKey;
  }
  return newRows;
};

export const createNewRowFromSpots = (
  spots: number,
  aggregateRow: FormattedRow,
  week: string
): PlanRow => {
  const totalSpend = aggregateRow.cost * spots;
  const rowCombined: FormattedRow = {
    company: aggregateRow.company,
    channel: aggregateRow.channel,
    plan_id: null,
    network: aggregateRow.network,
    week,
    dow: aggregateRow.dow,
    avail: aggregateRow.avail,
    rotation: aggregateRow.rotation,
    market: aggregateRow.market,
    media_classification: aggregateRow.media_classification,
    daypart_start: aggregateRow.daypart_start,
    daypart_end: aggregateRow.daypart_end,
    notes: null,
    length: aggregateRow.length,
    count: spots,
    cost: aggregateRow.cost,
    type: aggregateRow.type,
    key: '',
    order_id: null,
    traffic_id: null,
    isNewNetwork: true,
    isNewRow: true,
    is_plan_pending: true,
    campaign_id: aggregateRow.campaign_id,
    formattedType: '',
    totalSpend,
    impressions: 0,
    secondaryImpressions: 0,
    cpm: 0,
    trp: 0,
    rows: []
  };
  rowCombined.key = makeKeyWithUUID(rowCombined);
  return rowCombined;
};

// flatten rowsbyWeek into an array of rows so it can be iterated on all dates instead of one
export const flattenedRowsByWeek = (rowsByWeek: RowsByWeek): PlanRow[] =>
  Object.values(rowsByWeek).flat();
interface CalculateAudienceInfo {
  row: PlanRow | AudienceInfo;
  nielsenEstimates: NielsenEstimates;
  universeEstimate?: number;
  assumedClearanceRate?: number | undefined;
}

export interface AudienceInfo {
  avail: (typeof AVAILS)[number];
  network: string;
  dow: string[];
  daypart_start: string;
  daypart_end: string;
  type: string;
  cost: number;
  count: number;
}

export const compareDates = (rowsByWeek: RowsByWeek, mondayDates: string[]): boolean => {
  if (Object.keys(rowsByWeek).length === 0) {
    return false;
  }
  return mondayDates.every(date => date in rowsByWeek);
};

// Finds the first row carrying an edit for this specific kpi. Matching on "has any edit at all"
// would return an unedited value from whichever row happened to be edited for something else.
const findRowEditedFor = (
  rows: FormattedRow[],
  kpi: keyof FormattedRow
): FormattedRow | undefined => rows.find(row => R.has(String(kpi), row?.edits ?? {}));

// finds the pre-edit string metric
export const findOldMetricString = (rows: FormattedRow[], kpi: keyof FormattedRow): string =>
  String(findRowEditedFor(rows, kpi)?.[kpi] ?? '');

// finds the pre-edit metric
export const findOldMetricNumber = (rows: FormattedRow[], kpi: keyof FormattedRow): number =>
  Number(findRowEditedFor(rows, kpi)?.[kpi] ?? 0);

// finds if aggregated row has edits to particular kpi. Presence, not truthiness — an edit can
// legitimately be 0 (Regular is type 0, a count can be zeroed) or an empty string. Callers also
// pass `market`, which isn't a RowEdit field, so those rows still read as unedited.
export const isMultiWeekEdited = (rows: FormattedRow[], kpi: string): boolean =>
  rows.some(row => R.has(kpi, row?.edits ?? {}));

// finds if every row in the aggregate is a new row
export const isNewRowInAggregate = (rows: FormattedRow[]): boolean =>
  rows.length > 0 && rows.every(row => row.isNewRow);

/**
 * Calculates impressions, CPM, and TRP for a plan row.
 */
export const calculateAudienceInfo = ({
  row,
  nielsenEstimates,
  universeEstimate,
  assumedClearanceRate
}: CalculateAudienceInfo): { impressions: number; cpm: number; trp: number } => {
  const { avail, network, dow, daypart_start, daypart_end, type } = row;
  const spend = row.cost * row.count;
  // Deduped: a day listed twice would otherwise be counted twice in the audience average.
  const uniqueDays = new Set(dow);

  // Check if the rotation spans across 2 days (past midnight).
  let rotationOverlapsDays = false;
  if (daypart_end < daypart_start) {
    rotationOverlapsDays = true;
  }

  let totalAudience = 0;
  let timeBlocks = 0;
  for (const day of uniqueDays) {
    const networkDayEstimates: Record<string, number> | undefined = R.path(
      [network, day],
      nielsenEstimates
    );
    for (const time of R.keys(networkDayEstimates)) {
      if (!rotationOverlapsDays && time >= daypart_start && time <= daypart_end) {
        let audience = R.pathOr(0, [time], networkDayEstimates);
        if (avail === 'L') {
          audience *= LOCAL_AUDIENCE_CORRECTION_FACTOR;
        }
        totalAudience += audience;
        timeBlocks++;
      } else if (rotationOverlapsDays && time >= daypart_start) {
        let audience = R.pathOr(0, [time], networkDayEstimates);
        if (avail === 'L') {
          audience *= LOCAL_AUDIENCE_CORRECTION_FACTOR;
        }
        totalAudience += audience;
        timeBlocks++;
      } else if (rotationOverlapsDays && time <= daypart_end) {
        let audience = R.pathOr(0, [time], networkDayEstimates);
        if (avail === 'L') {
          audience *= LOCAL_AUDIENCE_CORRECTION_FACTOR;
        }
        totalAudience += audience;
        timeBlocks++;
      }
    }
  }

  const averageAudience = totalAudience / timeBlocks;
  // TODO: do we want to use impression overrides if they exist here?
  let impressions = averageAudience * row.count;
  let cpm = (spend / impressions) * 1000;
  if (cpm === Infinity) {
    cpm = 0;
  }

  if (assumedClearanceRate && type !== TYPES_MAP.Secured) {
    impressions *= assumedClearanceRate;
  }

  let trp;
  if (universeEstimate) {
    trp = (100 * (impressions / 1000)) / universeEstimate;
    if (assumedClearanceRate) {
      trp *= assumedClearanceRate;
    }
  }
  return { impressions: Math.round(impressions || 0), cpm: cpm || 0, trp: trp || 0 };
};

/**
 * Creates summary level spend totals by network, rotation, avail, and length.
 */
interface MakeSummaryData {
  rows: PlanRow[];
  nielsenEstimates: NielsenEstimates;
  creativeMap: CreativeMap | undefined;
  assumedClearanceRate?: number | undefined;
}
export const makeSummaryData = ({
  rows,
  nielsenEstimates,
  creativeMap,
  assumedClearanceRate
}: MakeSummaryData): SummaryData => {
  let totalSpend = 0;
  const spendByAvail: Record<string, number> = {};
  const spendByLength: Record<string, number> = {};
  const spendByNetwork: Record<string, number> = {};
  const spendByRotation: Record<string, number> = {};
  const spendByCreative: Record<string, number> = {};
  const spendByAvailLength: Record<string, Record<string, number>> = {};

  const impressionsByNetwork: Record<string, number> = {};
  const impressionsByRotation: Record<string, number> = {};
  const impressionsByLength: Record<string, number> = {};
  const impressionsByCreative: Record<string, number> = {};

  for (const row of rows) {
    const { cost, avail, length, network, rotation } = row;
    let { count } = row;
    let { impressions } = calculateAudienceInfo({ row, nielsenEstimates });
    // Secured inventory is guaranteed, so clearance doesn't apply to it. `calculateAudienceInfo`
    // encodes the same rule; this path scales the numbers itself and has to match.
    if (assumedClearanceRate && TYPES_TO_NAMES[row.type] !== 'Secured') {
      count *= assumedClearanceRate;
      impressions *= assumedClearanceRate;
    }
    const rotationPrefix = (rotation || '').split('(')[0].trim();
    const spend = cost * count;
    totalSpend += spend;
    spendByLength[length] = (spendByLength[length] || 0) + spend;
    spendByNetwork[network] = (spendByNetwork[network] || 0) + spend;
    spendByAvail[avail] = (spendByAvail[avail] || 0) + spend;
    spendByRotation[rotationPrefix] = (spendByRotation[rotationPrefix] || 0) + spend;
    spendByAvailLength[avail] = {
      ...(spendByAvailLength[avail] || {}),
      [length]: ((spendByAvailLength[avail] || {})[length] || 0) + spend
    };
    impressionsByNetwork[network] = (impressionsByNetwork[network] || 0) + impressions;
    impressionsByRotation[rotationPrefix] =
      (impressionsByRotation[rotationPrefix] || 0) + impressions;
    impressionsByLength[length] = (impressionsByLength[length] || 0) + impressions;

    if (row.creatives && creativeMap) {
      for (const creative of row.creatives) {
        const { isci, percent } = creative;
        // A rotation can reference a creative that's since been removed from the map. Fall back to
        // the raw ISCI so the spend still lands somewhere visible instead of crashing the summary.
        const creativeName = `${creativeMap[isci]?.name ?? isci} (${row.length}s)`;
        const adjustedSpend = (percent / 100) * spend;
        const adjustedImpressions = (percent / 100) * impressions;
        spendByCreative[creativeName] = (spendByCreative[creativeName] || 0) + adjustedSpend;
        impressionsByCreative[creativeName] =
          (impressionsByCreative[creativeName] || 0) + adjustedImpressions;
      }
    }
  }

  const byAvailLength: SpendByAvailLength[] = [];
  for (const avail of R.keys(spendByAvailLength)) {
    const totalSpendByAvail = spendByAvail[avail];
    const availRow: SpendByAvailLength = {
      avail,
      totalPercentByAvail: totalSpendByAvail / totalSpend,
      totalSpendByAvail
    };
    for (const length of R.keys(spendByAvailLength[avail])) {
      availRow[Number(length)] = spendByAvailLength[avail][length] / totalSpend;
    }
    byAvailLength.push(availRow);
  }

  const byNetwork = getSpendAsArray(spendByNetwork, impressionsByNetwork, totalSpend, 'network');
  const byRotation = getSpendAsArray(
    spendByRotation,
    impressionsByRotation,
    totalSpend,
    'rotation'
  );
  const byLength = getSpendAsArray(spendByLength, impressionsByLength, totalSpend, 'length');
  const byCreative = getSpendAsArray(
    spendByCreative,
    impressionsByCreative,
    totalSpend,
    'creative'
  );

  return {
    totalSpend,
    byLength,
    byNetwork,
    byRotation,
    byCreative,
    byAvailLength
  };
};

export interface SpendObj {
  spend: number;
  percent: number;
  impressions: number;
  cpm: number;
  // The grouping key — `network`, `rotation`, `length`, or `creative` depending on the caller.
  [property: string]: string | number;
}
const getSpendAsArray = (
  spendByProperty: Record<string, number>,
  impressionsByProperty: Record<string, number>,
  totalSpend: number,
  propertyName: string
): SpendObj[] =>
  R.keys(spendByProperty)
    .map(property => {
      const spend = spendByProperty[property];
      const percent = spend / totalSpend;
      const impressions = impressionsByProperty[property];

      let cpm = (spend / impressions) * 1000;
      if (cpm === Infinity) {
        cpm = 0;
      }

      return {
        [propertyName]: property,
        spend,
        percent,
        impressions,
        cpm
      };
    })
    .sort((a, b) => b.spend - a.spend);

/**
 * Creates the metadata for a new row with placeholder values.
 */
export const makeNewRow = (
  rotationsAndPricing: RotationsAndPricing,
  network: string,
  week: string,
  company: string,
  isNewNetwork = false
): PlanRow => {
  const defaultRow: PlanRow = {
    plan_id: null,
    network,
    week,
    dow: ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'],
    avail: 'N',
    rotation: '',
    market: '',
    daypart_start: '',
    daypart_end: '',
    notes: null,
    length: 30,
    count: 0,
    cost: 0,
    type: 0,
    company,
    key: '',
    order_id: null,
    traffic_id: null,
    isNewNetwork,
    isNewRow: true,
    is_plan_pending: true,
    campaign_id: null
  };

  // We don't know what avails a network will have (N and/or L), so take whichever comes first and
  // use its first rotation item to fill in placeholder values when adding a new row.
  const [firstAvail] = R.keys(rotationsAndPricing[network]);
  const firstRotation = firstAvail ? rotationsAndPricing[network][firstAvail][0] : undefined;
  if (firstRotation) {
    defaultRow.rotation = firstRotation.rotationLabel || 'Not Selected';
    defaultRow.cost = firstRotation.cost30s;
    defaultRow.daypart_start = time24hr(firstRotation.daypartBegin);
    defaultRow.daypart_end = time24hr(firstRotation.daypartEnd);
    defaultRow.avail = firstRotation.avail;
    defaultRow.dow = firstRotation.daysOfWeek;
    defaultRow.type = firstRotation.status === 'secured' ? 1 : 0;
    defaultRow.market = firstRotation.market;
  }

  const key = makeKeyWithUUID(defaultRow);

  const rowWithKey = { ...defaultRow, key };

  return rowWithKey;
};

/**
 * Compares all plan rows to Rotations and Pricing to check if any rates need to be updated.
 */
export const checkUpdatedRates = (
  rotationsAndPricing: RotationsAndPricing,
  rows: PlanRow[]
): EditsMap => {
  const flattenedRatesMap: Record<string, number> = {};
  for (const network of R.keys(rotationsAndPricing)) {
    for (const avail of R.keys(rotationsAndPricing[network])) {
      for (const rotation of rotationsAndPricing[network][avail]) {
        const key = `${network}_${avail}_${rotation.rotationLabel}_${rotation.status}`;
        flattenedRatesMap[key] = rotation.cost30s;
      }
    }
  }

  const rateChanges: EditsMap = {};
  for (const row of rows) {
    const { cost, network, avail, rotation, length, key, type } = row;
    const currentRate = cost;
    // Prefer the rate filed under the row's own status, but fall back to buyable for everything
    // that isn't Secured. `rotationsAndPricing` only carries LINEAR_RATE_STATUSES
    // (buyable/secured/general), so Remnant and Upfront rows never match on their own status —
    // without the fallback they'd be skipped entirely and never checked for a stale rate.
    const status = TYPE_TO_STATUS_IN_NETWORK_PRICING[type];
    let mostRecentRate = flattenedRatesMap[`${network}_${avail}_${rotation}_${status}`];
    if (!mostRecentRate && status !== 'secured') {
      mostRecentRate = flattenedRatesMap[`${network}_${avail}_${rotation}_buyable`];
    }
    if (!mostRecentRate) {
      continue;
    }

    // If length is 15s or 60s, halve or double most recent rate to accurately compare.
    // Rotations and Pricing refers to the 30s rates.
    if (length === 15) {
      mostRecentRate = Math.round(mostRecentRate / 2);
    }
    if (length === 60) {
      mostRecentRate *= 2;
    }

    // If the two rates aren't equal, we'll want to update.
    if (currentRate !== mostRecentRate) {
      rateChanges[key] = { ...row, edits: { cost: mostRecentRate } };
    }
  }

  return rateChanges;
};

export interface CreativeOption extends CreativeMapItem {
  /** The ISCI. `name` is overwritten so the option lists key and label off the ISCI. */
  name: string;
  /** The human-readable creative name. */
  creative: string;
  color: string;
  notLive: boolean;
}

/**
 * Decides which creatives to show in the creative allocation modal for a plan row.
 */
export const getCreativeOptions = (
  planRow: PlanRow,
  creativeMap: CreativeMap,
  colorMap: Record<string, string>
): CreativeOption[] => {
  if (!creativeMap) {
    return [];
  }
  const { avail: planRowAvail, length: planRowLength, creatives: planRowCreatives, week } = planRow;
  const creativeMapIscis: string[] = Object.values(creativeMap).map(creative => creative.isci);

  // Creatives that are currently in the rotation.
  const currentCreativesInRotation: Record<string, boolean> = {};
  if (planRowCreatives) {
    for (const creative of planRowCreatives) {
      currentCreativesInRotation[creative.isci] = true;
    }
  }

  if (creativeMapIscis.length) {
    const options: CreativeOption[] = [];
    for (const isci of creativeMapIscis) {
      const creative = creativeMap[isci];
      const { avail, length } = creative;

      const creativeIsLive = isCreativeLive(
        creative,
        week,
        Dfns.format(DATE_FORMAT, Dfns.addDays(7, new Date(week)))
      );

      const showCreative = creativeIsLive && avail === planRowAvail && length === planRowLength;

      // Include a creative in the modal if it matches the requirements (same avail, same length, and is live)
      // or if the creative is currently in the rotation. The second condition is for the case when a creative
      // is in the rotation but then gets set to not-live in the creative map after the fact. We still want
      // to show it so that it can be removed, but will give a visual indication in the modal.
      if (showCreative || currentCreativesInRotation[isci]) {
        options.push({
          ...creative,
          name: isci,
          creative: creative.name,
          color: colorMap[isci] || 'white',
          notLive: !showCreative && currentCreativesInRotation[isci]
        });
      }
    }
    return R.sortBy(R.prop('isci'), options);
  }
  return [];
};

/**
 * Convert days of week from v1 schema to v2 schema format
 * i.e. MTuWThF----  => ["M", "Tu", "W", "Th", "F"]
 */
export const convertDaysOfWeekStringToArray = (dow: string): string[] => {
  // Scanned rather than read at fixed offsets. The v1 format is padded to a fixed width, but this
  // also parses a hand-typed cell like "MWF" from an imported spreadsheet, which the offset
  // version silently decoded as the wrong set of days.
  const dayCodes = new Set<string>(DAYS_OF_WEEK);
  const found = new Set<string>();
  let index = 0;
  while (index < dow.length) {
    const twoCharCode = dow.slice(index, index + 2);
    const oneCharCode = dow.slice(index, index + 1);
    if (dayCodes.has(twoCharCode)) {
      found.add(twoCharCode);
      index += 2;
    } else if (dayCodes.has(oneCharCode)) {
      found.add(oneCharCode);
      index += 1;
    } else {
      // Padding ("-") or anything unrecognized.
      index += 1;
    }
  }

  return DAYS_OF_WEEK.filter(day => found.has(day));
};

/**
 * Renders impression overrides as `Demo · Measurement: 1,234` pairs. The shape is nested two deep,
 * so `JSON.stringify` on it is unreadable in the places we only need to show the values.
 */
export const formatLinearImpressionOverrides = (overrides: LinearImpressionOverrides): string =>
  Object.keys(overrides)
    .flatMap(demo =>
      Object.keys(overrides[demo]).map(
        measurement => `${demo} · ${measurement}: ${overrides[demo][measurement].toLocaleString()}`
      )
    )
    .join(', ');

/*
 * Merges the impression override edits into the current impression overrides.
 */
export const deepMergeLinearImpressionOverrides = (
  current: LinearImpressionOverrides,
  edits: LinearImpressionOverrides
): LinearImpressionOverrides => {
  const result: LinearImpressionOverrides = { ...current };

  Object.keys(edits).forEach(demo => {
    if (!result[demo]) {
      result[demo] = {};
    }
    Object.keys(edits[demo]).forEach(measurement => {
      const value = edits[demo][measurement];
      result[demo][measurement] = value;
    });
  });

  return result;
};

/** How a set of rotation shares stands against the 100% a rotation has to total. */
export interface RotationTotal {
  total: number;
  isValidTotal: boolean;
  /** How far off 100% it is, for the error message. */
  offBy: number;
}

/**
 * The share arithmetic every rotation editor agrees on. What each editor does with it does not
 * transfer -- a blanket rotation with nothing allocated is unsaveable, while a per-line week with
 * nothing allocated is simply not being edited, and only the per-line one caps creatives at the
 * row's spot count -- so those rules stay with their editors and only the sum lives here.
 */
export const getRotationTotal = (shares: { percent: number }[]): RotationTotal => {
  const total = shares.reduce((sum, { percent }) => sum + (percent || 0), 0);
  return { total, isValidTotal: total === 100, offBy: Math.abs(100 - total) };
};
